/**
 * `useForestHistory` — project-wide ("forest") activity stream for the Home
 * dashboard's `<ProjectActivityFeed/>` (#1043, round-4 item ⑤.c, 2026-05-28).
 *
 * The feed used to read ONE root's subtree (`useNodeHistory(teamId, roots[0].id,
 * "subtree")`), which silently dropped activity from every OTHER outcome root in
 * a multi-outcome project — QA-B finding B1 measured ~80% of events missing in a
 * 5-outcome team. This hook fixes that by fanning out one subtree-history fetch
 * per root and merging the streams client-side.
 *
 * Reuse, never rebuild (`feedback_taskmap_clean_code`): we do NOT duplicate the
 * endpoint or the cache key. We mount one TanStack `useQuery` per root via
 * `useQueries`, with EXACTLY the same `queryKey` shape (`NODE_HISTORY_KEY(...,
 * "subtree")`) and `queryFn` URL that `useNodeHistory` uses — so the
 * QueryClient deduplicates with the existing per-root callers (the per-node
 * drawer history, the canvas history rail, the rollup, etc.). A second mount
 * for the same root pulls from cache, not the network.
 *
 * Aggregation:
 *   - `events`: concat across roots, deduplicated by `event.id` (a defensive
 *     guard — overlap shouldn't happen for distinct roots, but a stale or
 *     racy fetch could repeat an event), then sorted DESCENDING by
 *     `occurred_at` to match the feed's existing newest-first presentation.
 *   - `isLoading`: ANY root is still loading (the feed shows its loading state
 *     until every root is in).
 *   - `isError`: ANY root errored (the feed shows its calm "unavailable"
 *     state).
 *
 * Empty inputs (`teamId == null` OR `rootIds.length === 0`) return a stable
 * empty result with no fetches — matching `useNodeHistory`'s no-team behaviour
 * and the feed's "no outcome yet" empty state.
 *
 * Isolation (`taskmap ⊥ scheduler`): taskmap-local imports only.
 */

import { useMemo } from "react";

import { useQueries } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";
import {
  type HistoryScope,
  NODE_HISTORY_KEY,
  type NodeHistoryItem,
  type UseNodeHistoryResult,
} from "./useNodeHistory";

type NodeHistoryResponse = components["schemas"]["NodeHistoryResponse"];

const STALE_TIME_MS = 30_000;

const EMPTY_RESULT: UseNodeHistoryResult = {
  events: [],
  isLoading: false,
  isError: false,
};

/**
 * Fetch the subtree-history of every root in `rootIds` (default scope
 * `"subtree"`) and merge them into one stream — newest-first, deduplicated by
 * event id. Cache keys mirror `useNodeHistory`'s exactly so per-root callers
 * dedup through the same QueryClient.
 *
 * No-ops cleanly when `teamId` is undefined or `rootIds` is empty (no roots
 * yet) — returns a stable empty result with no fetches.
 */
export function useForestHistory(
  teamId: string | undefined,
  rootIds: readonly string[],
  scope: HistoryScope = "subtree",
): UseNodeHistoryResult {
  // Stable empty result when there's nothing to fetch. Memoised so a parent
  // re-render doesn't churn the consumer's deps.
  const hasInput = teamId != null && rootIds.length > 0;

  const queries = useQueries({
    queries: hasInput
      ? rootIds.map((rootId) => ({
          queryKey: NODE_HISTORY_KEY(teamId as string, rootId, scope),
          queryFn: () =>
            apiFetch<NodeHistoryResponse>(
              // The "node" default omits the param to stay byte-identical to
              // every other caller's cache key + URL — that's how QueryClient
              // dedup fires.
              `/api/teams/${teamId as string}/tasks/nodes/${rootId}/history${
                scope === "subtree" ? "?scope=subtree" : ""
              }`,
            ),
          staleTime: STALE_TIME_MS,
          // Match `useNodeHistory` precisely: a not-yet-deployed endpoint
          // should fail fast to the calm "unavailable" state rather than spin.
          retry: false,
        }))
      : [],
  });

  return useMemo<UseNodeHistoryResult>(() => {
    if (!hasInput) return EMPTY_RESULT;

    const isLoading = queries.some((q) => q.isLoading);
    const isError = queries.some((q) => q.isError);

    // Merge + dedup by event id. A stale/racy fetch could replay an id from a
    // sibling root; one entry wins, deterministically.
    const seen = new Set<string>();
    const merged: NodeHistoryItem[] = [];
    for (const q of queries) {
      const events = Array.isArray(q.data?.events) ? q.data.events : [];
      for (const e of events) {
        if (seen.has(e.id)) continue;
        seen.add(e.id);
        merged.push(e);
      }
    }
    // Newest-first across the merged set, matching the feed's presentation.
    merged.sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at));

    return { events: merged, isLoading, isError };
  }, [hasInput, queries]);
}
